import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Send, Mail, Clock, CheckCircle, XCircle, RotateCw, AlertTriangle, Ban } from 'lucide-react';
import { useSendApprovalEmail } from '@/modules/financiamento/hooks/useSendApprovalEmail';
import { useNetApprovalStatus, type NetApprovalStatus, type ApprovalSlaStatusDetalhado } from '@/modules/financiamento/hooks/useNetApprovalStatus';
import { useNetExternalApprovals } from '@/modules/financiamento/hooks/useNetExternalApprovals';
import { useNetContratantePerfil, type ContratanteContatos } from '@/modules/financiamento/hooks/useNetContratantePerfil';

interface ApprovalEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boletimId: string;
  contratoId: string;
  origem?: 'automatico' | 'manual';
}

const STATUS_CONFIG: Record<ApprovalSlaStatusDetalhado, {
  label: string;
  icon: React.ElementType;
  badgeVariant: 'default' | 'secondary' | 'destructive' | 'outline';
  className: string;
}> = {
  pendente: { label: 'Pendente', icon: Clock, badgeVariant: 'secondary', className: 'text-[10px]' },
  pendente_atencao: { label: 'Pendente >24h', icon: AlertTriangle, badgeVariant: 'outline', className: 'text-[10px] border-amber-400 text-amber-600' },
  expirado: { label: 'Expirado', icon: XCircle, badgeVariant: 'destructive', className: 'text-[10px]' },
  ignorado_reiterado: { label: 'Ignorado', icon: Ban, badgeVariant: 'destructive', className: 'text-[10px]' },
  respondido: { label: 'Respondido', icon: CheckCircle, badgeVariant: 'outline', className: 'text-[10px] border-green-400 text-green-600' },
};

const MAX_SENDS_PER_BOLETIM = 3;
const MIN_RESEND_HOURS = 12;

export function ApprovalEmailDialog({
  open, onOpenChange, boletimId, contratoId, origem = 'manual',
}: ApprovalEmailDialogProps) {
  const [email, setEmail] = useState('');
  const sendMutation = useSendApprovalEmail();
  const { data: statuses } = useNetApprovalStatus(boletimId);
  const { data: approvals } = useNetExternalApprovals(boletimId);
  const { data: contratantePerfil } = useNetContratantePerfil(contratoId);

  const latestStatus = statuses?.find(s => s.is_latest);
  const totalSends = latestStatus?.quantidade_envios_por_boletim ?? 0;
  const horasSinceLastSend = latestStatus?.horas_desde_envio ?? Infinity;

  // Resend rules
  const canResend = totalSends < MAX_SENDS_PER_BOLETIM && horasSinceLastSend >= MIN_RESEND_HOURS;
  const shouldSuggestResend = latestStatus &&
    (latestStatus.status === 'pendente_atencao' || latestStatus.status === 'expirado') &&
    canResend;

  const resendBlockReason = !canResend
    ? totalSends >= MAX_SENDS_PER_BOLETIM
      ? `Limite de ${MAX_SENDS_PER_BOLETIM} envios atingido`
      : `Aguardar ${MIN_RESEND_HOURS - Math.floor(horasSinceLastSend)}h para reenviar`
    : null;

  // Pre-fill email from contratante perfil
  useEffect(() => {
    if (!open) return;
    if (contratantePerfil?.contatos) {
      const contatos = contratantePerfil.contatos as ContratanteContatos;
      const foundEmail = contatos?.email || contatos?.email_principal || '';
      if (foundEmail) {
        setEmail(foundEmail);
        return;
      }
    }
  }, [open, contratantePerfil]);

  const handleSend = () => {
    if (!email.trim()) return;
    sendMutation.mutate(
      { boletimId, emailDestinatario: email.trim(), origem },
      { onSuccess: () => onOpenChange(false) }
    );
  };

  const lastApproval = approvals?.[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Enviar Aprovação por E-mail
          </DialogTitle>
          <DialogDescription>
            Um e-mail será enviado com links seguros para aprovar ou reprovar o boletim.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Resend suggestion banner */}
          {shouldSuggestResend && (
            <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 text-sm">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
              <span className="text-amber-800 dark:text-amber-200">
                {latestStatus.status === 'expirado'
                  ? `Token expirado há ${latestStatus.horas_desde_envio - 48}h. Reenvio recomendado.`
                  : `Sem resposta há ${latestStatus.horas_desde_envio}h. Considere reenviar.`}
              </span>
            </div>
          )}

          {/* Email input */}
          <div className="space-y-2">
            <Label htmlFor="approval-email">E-mail do contratante</Label>
            <Input
              id="approval-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@contratante.com"
              disabled={sendMutation.isPending}
            />
            <p className="text-xs text-muted-foreground">
              O e-mail pode ser editado antes do envio. O endereço efetivamente utilizado será registrado.
            </p>
          </div>

          {/* Send history with SLA status */}
          {statuses && statuses.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                Histórico de envios ({totalSends}/{MAX_SENDS_PER_BOLETIM})
              </Label>
              <div className="max-h-40 overflow-y-auto space-y-1.5">
                {statuses.map((s) => {
                  const cfg = STATUS_CONFIG[s.status_detalhado] || STATUS_CONFIG.pendente;
                  const StatusIcon = cfg.icon;
                  return (
                    <div key={s.token_id} className="flex items-center justify-between text-xs bg-muted/50 rounded px-3 py-2">
                      <div className="flex flex-col gap-0.5 truncate flex-1 mr-2">
                        <span className="truncate font-medium">{s.email_destinatario}</span>
                        <span className="text-muted-foreground">
                          Enviado em {new Date(s.created_at).toLocaleString('pt-BR')} · há {s.horas_desde_envio}h
                        </span>
                      </div>
                      <Badge variant={cfg.badgeVariant} className={cfg.className}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {cfg.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Current approval status */}
          {lastApproval && (
            <div className="bg-muted/30 rounded-md p-3 text-sm">
              <span className="text-muted-foreground">Última decisão: </span>
              <Badge variant={lastApproval.status === 'aprovado' ? 'default' : 'destructive'}>
                {lastApproval.status}
              </Badge>
              {lastApproval.email_destinatario && (
                <span className="text-xs text-muted-foreground ml-2">
                  por {lastApproval.email_destinatario}
                </span>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <div className="flex flex-col items-end gap-1">
            <Button
              onClick={handleSend}
              disabled={!email.trim() || sendMutation.isPending || (totalSends > 0 && !canResend)}
            >
              {sendMutation.isPending ? (
                <><RotateCw className="h-4 w-4 mr-1 animate-spin" /> Enviando...</>
              ) : totalSends > 0 ? (
                <><Send className="h-4 w-4 mr-1" /> Reenviar</>
              ) : (
                <><Send className="h-4 w-4 mr-1" /> Enviar</>
              )}
            </Button>
            {resendBlockReason && totalSends > 0 && (
              <span className="text-[10px] text-muted-foreground">{resendBlockReason}</span>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
